import React from 'react';
import styled from 'styled-components';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import TitleBar from '../../components/TitleBar';
import buttonTheme from '../../res/styles/theme_button.module.scss';
import { getSettingList, saveSettings } from '../../service/SettingService';
import { HorizontalDiv, VerticalDiv } from '../../components/Common';
import colors from '../../res/styles/Colors';
import dimension from '../../res/styles/Responsive';
import { toastError, toastSuccess } from '../../ui/ToastHelper';
import { ControlPanelPage, Input } from './components/Common';

const diseasesKey = 'DiseasesDescriptions';

export default function DiseasesList() {
    const [diseases, setDiseases] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [editingId, setEditingId] = React.useState(null);
    const [editValue, setEditValue] = React.useState('');

    const fetchDiseases = React.useCallback(async () => {
        try {
            const fetchedSettings = await getSettingList([diseasesKey]);
            if (fetchedSettings.length > 0 && fetchedSettings[0].value) {
                // Parse diseases from pipe-separated string
                const diseasesString = fetchedSettings[0].value;
                const diseasesArray = diseasesString.split('|').map((name, index) => ({
                    id: index,
                    name: name.trim()
                }));
                setDiseases(diseasesArray);
            } else {
                setDiseases([]);
            }
        } catch (error) {
            console.error('Error fetching diseases:', error);
            toastError('An error occurred while fetching diseases');
            setDiseases([]);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchDiseases();
    }, [fetchDiseases]);

    const handleAdd = () => {
        const newDisease = {
            id: Date.now(),
            name: 'New Disease',
            code: ''
        };
        setDiseases([...diseases, newDisease]);
        setEditingId(newDisease.id);
        setEditValue(newDisease.name);
    };

    const handleEdit = (disease) => {
        setEditingId(disease.id);
        setEditValue(disease.name);
    };

    const handleRemove = (diseaseId) => {
        setDiseases(diseases.filter(d => d.id !== diseaseId));
        if (editingId === diseaseId) {
            setEditingId(null);
            setEditValue('');
        }
    };

    const handleSaveEdit = (diseaseId) => {
        if (editValue.trim() === '') {
            toastError('Disease name cannot be empty');
            return;
        }

        setDiseases(diseases.map(d =>
            d.id === diseaseId ? { ...d, name: editValue.trim() } : d
        ));
        setEditingId(null);
        setEditValue('');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };

    const handleSaveAll = () => {
        if (editingId !== null) {
            toastError('Please save or cancel the current edit before saving all');
            return;
        }

        // Convert diseases array to pipe-separated string
        const diseasesString = diseases.map(d => d.name).join('|');

        const newSetting = {
            key: diseasesKey,
            value: diseasesString
        };

        saveSettings(
            [newSetting],
            () => {
                toastSuccess('Diseases list successfully saved');
            },
            () => {
                toastError('An error occurred while saving diseases list');
            }
        );
    };

    return (
        <div>
            <NavigationBar />
            <TitleBar title='Diseases List' />
            <ControlPanelPage>
                <StyledContainer>
                    <StyledButtonsRow>
                        <StyledButton className={buttonTheme.green} onClick={handleAdd}>
                            ADD DISEASE
                        </StyledButton>
                        <StyledButton className={buttonTheme.green} onClick={handleSaveAll}>
                            SAVE CHANGES
                        </StyledButton>
                    </StyledButtonsRow>

                    {!loading && (
                        <>
                            {diseases.length === 0 ? (
                                <EmptyMessage>No diseases found. Click &quot;ADD DISEASE&quot; to add a new one.</EmptyMessage>
                            ) : (
                                <DiseasesTable>
                                    <TableHeader>
                                        <HeaderCell style={{ width: '60%' }}>Disease Name</HeaderCell>
                                        <HeaderCell style={{ width: '40%' }}>Actions</HeaderCell>
                                    </TableHeader>
                                    <TableBody>
                                        {diseases.map((disease) => (
                                            <TableRow key={disease.id}>
                                                <TableCell>
                                                    {editingId === disease.id ? (
                                                        <StyledInput
                                                            type='text'
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <span>{disease.name}</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <ActionsContainer>
                                                        {editingId === disease.id ? (
                                                            <>
                                                                <ActionButton
                                                                    className={buttonTheme.green}
                                                                    onClick={() => handleSaveEdit(disease.id)}>
                                                                    OK
                                                                </ActionButton>
                                                                <ActionButton
                                                                    className={buttonTheme.gray}
                                                                    onClick={handleCancelEdit}>
                                                                    CANCEL
                                                                </ActionButton>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ActionButton
                                                                    className={buttonTheme.green}
                                                                    onClick={() => handleEdit(disease)}>
                                                                    EDIT
                                                                </ActionButton>
                                                                <ActionButton
                                                                    className={buttonTheme.red}
                                                                    onClick={() => handleRemove(disease.id)}>
                                                                    REMOVE
                                                                </ActionButton>
                                                            </>
                                                        )}
                                                    </ActionsContainer>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </DiseasesTable>
                            )}
                        </>
                    )}
                </StyledContainer>
            </ControlPanelPage>
        </div>
    );
}

const StyledContainer = styled(VerticalDiv)`
    width: 95%;
    max-width: 900px;
    margin: 0 auto;
    height: calc(81vh - 50px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding-bottom: 20px;

    @media ${dimension.md} {
        width: 80%;
        padding-bottom: 30px;
    }

    @media ${dimension.lg} {
        width: 70%;
    }
`;

const StyledButtonsRow = styled(HorizontalDiv)`
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 10px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        flex-direction: column;
        margin-bottom: 15px;
    }
`;

const StyledButton = styled.button`
    flex: 1;
    min-width: 150px;
    padding: 10px 20px;
    
    @media (max-width: 768px) {
        width: 100%;
        padding: 12px 20px;
        font-size: 14px;
    }
`;

const EmptyMessage = styled.p`
    text-align: center;
    color: ${colors.darker_gray};
    font-size: 16px;
    margin-top: 50px;

    @media (max-width: 768px) {
        font-size: 14px;
        margin-top: 30px;
        padding: 0 10px;
    }
`;

const DiseasesTable = styled.div`
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const TableHeader = styled.div`
    display: flex;
    background-color: #e0e0e0;
    color: black;
    font-weight: bold;
    padding: 15px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        display: none;
    }
`;

const HeaderCell = styled.div`
    padding: 0 10px;
`;

const TableBody = styled.div`
    overflow-y: auto;
    flex: 1;
`;

const TableRow = styled.div`
    display: flex;
    border-bottom: 1px solid ${colors.light_gray};
    padding: 15px;
    transition: background-color 0.2s;

    &:hover {
        background-color: ${colors.light_gray};
    }

    &:last-child {
        border-bottom: none;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        padding: 12px;
        gap: 10px;
    }
`;

const TableCell = styled.div`
    display: flex;
    align-items: center;
    padding: 0 10px;

    &:first-child {
        width: 60%;
    }

    &:last-child {
        width: 40%;
    }

    @media (max-width: 768px) {
        padding: 0;
        width: 100% !important;

        &:first-child {
            font-weight: 600;
            font-size: 15px;
        }
    }
`;

const StyledInput = styled(Input)`
    width: 100%;
    padding: 5px 10px;
`;

const ActionsContainer = styled(HorizontalDiv)`
    gap: 10px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        gap: 8px;
        justify-content: flex-start;
    }
`;

const ActionButton = styled.button`
    padding: 5px 15px;
    font-size: 12px;
    min-width: 70px;

    @media (max-width: 768px) {
        padding: 8px 16px;
        font-size: 13px;
        min-width: 80px;
    }
`;

